import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import InputBase from '@mui/material/InputBase';
import clsx from 'clsx';
import { Day, DayContent, DayPicker, useActiveModifiers } from 'react-day-picker';
import { eachDayOfInterval, endOfDay, endOfMonth, isEqual, isSameDay, startOfDay, startOfMonth, } from 'date-fns';
import { getVirtualRange } from './getVirtualRange';
import { formatDate } from './date-fns';
import 'react-day-picker/dist/style.css';
import styled from '@mui/material/styles/styled';
import COLOR from '../theme/colors';
const css = `
.rdp {
  font-family: 'Roboto', sans-serif;
  --rdp-accent-color: #4388ff;
}

.rdp-head_cell {
  font-weight: normal;
  color: rgba(0, 0, 0, 0.38);
}

.rdp-row {
  margin-top: 2px;
  margin-bottom: 2px;
}

.rdp-cell {
  padding-top: 2px;
  padding-bottom: 2px;
}

.rdp-cell:first-child .rdp-day_selected.rdp-day_range_end:not(.rdp-day_range_start) {
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-cell:first-child .rdp-day_is_in_virtual_range>.rdp-day {
  border-left: 2px dashed rgba(0, 0, 0, 0.12);
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-cell:first-child .rdp-day_selected {
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-cell:last-child .rdp-day_selected {
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}

.rdp-cell:last-child .rdp-day_range_start:not(.rdp-day_range_end) {
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}

.rdp-cell:last-child .rdp-day_is_in_virtual_range>.rdp-day {
  border-right: 2px dashed rgba(0, 0, 0, 0.12);
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}

.rdp-day_start_of_month .rdp-day_selected.rdp-day_range_end:not(.rdp-day_range_start) {
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-day {
  font-size: 0.75rem;
  border-top: 1px dashed transparent;
  border-bottom: 1px dashed transparent;
}

.rdp-day_is_in_virtual_range>.rdp-day {
  border-top: 2px dashed rgba(0, 0, 0, 0.12);
  border-bottom: 2px dashed rgba(0, 0, 0, 0.12);
  border-radius: 0;
}

.rdp-day_is_in_virtual_range.rdp-day_start_of_month>.rdp-day {
  border-left: 2px dashed rgba(0, 0, 0, 0.12);
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-day_is_in_virtual_range.rdp-day_end_of_month>.rdp-day {
  border-right: 2px dashed rgba(0, 0, 0, 0.12);
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}

.rdp-day_start_of_month .rdp-day {
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}

.rdp-day_end_of_month .rdp-day {
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}

.rdp-day.rdp-day_selected {
  background-color: rgba(25, 118, 210, 0.12);
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.75rem;
}

.rdp-day_selected.rdp-day_range_start,
.rdp-day_selected.rdp-day_range_end {
  border-width: 0;
}

.rdp-day_selected.rdp-day_range_start>div {
  background-color: var(--rdp-accent-color);
  color: #fff;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.rdp-day_selected.rdp-day_range_end>div {
  background-color: var(--rdp-accent-color);
  color: #fff;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}`;
function DayContentComponent(props) {
    const { activeModifiers } = props;
    const { isInVirtualRange } = activeModifiers;
    return (_jsx("div", Object.assign({ className: clsx({ ['rdp-day_in_virtual_range']: isInVirtualRange }) }, { children: _jsx(DayContent, Object.assign({}, props)) })));
}
function DayComponent(props) {
    const { date, displayMonth } = props;
    const { isInVirtualRange } = useActiveModifiers(date, displayMonth);
    const isStartOfMonth = isEqual(startOfMonth(date), date);
    const isEndOfMonth = isEqual(endOfMonth(date), endOfDay(date));
    return (_jsx("div", Object.assign({ className: clsx({
            ['rdp-day_is_in_virtual_range']: isInVirtualRange,
            ['rdp-day_start_of_month']: isStartOfMonth,
            ['rdp-day_end_of_month']: isEndOfMonth,
        }) }, { children: _jsx(Day, Object.assign({}, props)) })));
}
const InputDate = styled(InputBase)(({ theme }) => {
    return {
        position: 'relative',
        fontSize: 16,
        width: '100%',
        padding: '8px 16px',
        color: COLOR.textColor.primary,
        transition: theme.transitions.create([
            'border-color',
            'background-color',
            'box-shadow',
        ]),
        '& input': {
            padding: 0,
            color: COLOR.textColor.primary,
        },
    };
});
export const DateRangePicker = ({ onChange, value }) => {
    // const initialDate = useRef(value)
    const [selectedDate, setSelectedDate] = useState(value);
    const [focused, setFocused] = useState(false);
    const [hoveredDate, setHoveredDate] = useState(null);
    const startDate = selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate[0];
    const endDate = selectedDate === null || selectedDate === void 0 ? void 0 : selectedDate[1];
    useEffect(() => {
        setSelectedDate(value);
    }, [value]);
    const virtualRange = useMemo(() => {
        if (!hoveredDate)
            return [];
        const range = getVirtualRange(hoveredDate, startDate, endDate);
        if (range.length === 0)
            return [];
        return eachDayOfInterval({ start: range[0], end: range[1] });
    }, [endDate, hoveredDate, startDate]);
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setFocused(true);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setFocused(false);
        if (onChange) {
            onChange(selectedDate);
        }
    };
    const open = Boolean(anchorEl);
    const handleDayClick = (date) => {
        let start = startDate;
        let end = endDate;
        if (start) {
            if (end) {
                start = date;
                end = undefined;
            }
            else {
                end = date;
            }
        }
        else {
            start = date;
        }
        if (start && end) {
            if (Number(start) > Number(end)) {
                const temp = start;
                start = end;
                end = temp;
            }
        }
        let range = [start, end];
        if (start && end && isSameDay(start, end)) {
            range = [startOfDay(start), endOfDay(end)];
        }
        setSelectedDate(range);
    };
    const after = new Date();
    const before = new Date(2018, 1, 1);
    const afterMatcher = { after };
    const beforeMatcher = { before };
    const disabledDays = [afterMatcher, beforeMatcher];
    return (_jsxs(Box, Object.assign({ width: '100%', sx: {
            overflow: 'hidden',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
                borderColor: '#404042',
            },
        }, className: focused ? 'focused' : '' }, { children: [_jsx("div", Object.assign({ onClick: handleClick, style: { width: '100%' } }, { children: _jsxs(Box, Object.assign({ style: { display: 'flex' } }, { children: [_jsx(Box, Object.assign({ style: { marginRight: 4, width: '50%' } }, { children: _jsx(InputDate, { fullWidth: true, value: startDate ? formatDate(startDate) : '', inputProps: { placeholder: 'MM/DD/YYYY', readOnly: true, color: COLOR.textColor.primary }, sx: {
                                    borderRadius: '4px',
                                    border: '1px solid #C8C9CC',
                                    '&.focused': {
                                        '&::after': {
                                            content: "''",
                                            position: 'absolute',
                                            inset: 0,
                                            border: `2px solid ${COLOR.commonColor.primary}`,
                                            borderRadius: '4px',
                                            margin: '-1px'
                                        },
                                    },
                                }, className: focused ? 'focused' : '' }) })), _jsx(Box, Object.assign({ style: { marginLeft: 4, width: '50%' } }, { children: _jsx(InputDate, { fullWidth: true, value: endDate ? formatDate(endDate) : '', inputProps: { placeholder: 'MM/DD/YYYY', readOnly: true, color: COLOR.textColor.primary }, sx: {
                                    borderRadius: '4px',
                                    border: '1px solid #C8C9CC',
                                    '&.focused': {
                                        '&::after': {
                                            content: "''",
                                            position: 'absolute',
                                            inset: 0,
                                            border: `2px solid ${COLOR.commonColor.primary} !important`,
                                            borderRadius: '3px',
                                            margin: '-1px'
                                        },
                                    },
                                }, className: focused ? 'focused' : '' }) }))] })) })), _jsxs(Popover, Object.assign({ open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                }, slotProps: {
                    // @ts-ignore
                    paper: {
                        sx: {
                            marginTop: '1px',
                        },
                    },
                } }, { children: [_jsx("style", { children: css }), _jsx(DayPicker, { mode: "range", selected: { from: startDate, to: endDate }, onDayClick: handleDayClick, captionLayout: "dropdown-buttons", fromYear: 2015, toYear: 2050, numberOfMonths: 2, components: {
                            Day: DayComponent,
                            DayContent: DayContentComponent,
                        }, onDayPointerEnter: setHoveredDate, onDayPointerLeave: () => setHoveredDate(null), modifiers: { isInVirtualRange: virtualRange }, disabled: disabledDays })] }))] })));
};
